import { requestStatusCodes } from "./state-words.js";
// The status code of every request state lives with the words of the state (`state-words.ts`, a file that imports no value). It is
// re-exported here, where the Action Center names it.
export { requestStatusCodes };
/**
 * The customer-facing Action projection. It is derived, never stored: a pure function over the
 * state Postgres already holds, so it cannot drift from the request or the handoff it describes.
 * Ids are stable and name the owning entity (`concierge_request:<uuid>`), so they survive every
 * transition of the source. SAVED items come from Phase 2.4 through `projectSavedItem`.
 */
export const actionBuckets = Object.freeze([
    "NEEDS_YOU",
    "WITH_CONCIERGE",
    "CONFIRMED",
    "SAVED",
]);
export const actionSourceKinds = Object.freeze([
    "CONCIERGE_REQUEST",
    "HANDOFF",
    "SAVED_ITEM",
    // CHT-738: a payment request that no concierge request owns. Appended; the v1 projection never emits it.
    "PAYMENT_REQUEST",
]);
/** `null`: a closed request is history, not an active item (it is never an item needing attention). */
const REQUEST_BUCKETS = {
    DRAFT: "NEEDS_YOU",
    SUBMITTED: "WITH_CONCIERGE",
    ASSIGNED: "WITH_CONCIERGE",
    IN_PROGRESS: "WITH_CONCIERGE",
    AWAITING_CUSTOMER: "NEEDS_YOU",
    PAYMENT_REQUIRED: "NEEDS_YOU",
    CONFIRMED: "CONFIRMED",
    COMPLETED: null,
    CANCELLED: null,
    UNAVAILABLE: null,
    DECLINED: null,
    EXPIRED: null,
};
// A waiting handoff looks the same whatever its target kind, UNASSIGNED included: the operational
// state is not leaked to the customer.
export const handoffStatusCodes = Object.freeze({
    REQUESTED: "CONNECTING_TO_CONCIERGE",
    WAITING: "CONNECTING_TO_CONCIERGE",
    ACCEPTED: "CONCIERGE_IN_CONVERSATION",
    RELEASED: null,
    CANCELLED: null,
});
export function projectConciergeRequest(source) {
    const bucket = REQUEST_BUCKETS[source.state];
    if (bucket === null)
        return null;
    return Object.freeze({
        id: `concierge_request:${source.id}`,
        sourceKind: "CONCIERGE_REQUEST",
        sourceId: source.id,
        bucket,
        statusCode: requestStatusCodes[source.state],
        title: source.title,
        updatedAt: source.updatedAt,
    });
}
export function projectHandoff(source) {
    const statusCode = handoffStatusCodes[source.status];
    if (statusCode === null)
        return null;
    return Object.freeze({
        id: `handoff:${source.id}`,
        sourceKind: "HANDOFF",
        sourceId: source.id,
        bucket: "WITH_CONCIERGE",
        statusCode,
        title: null,
        updatedAt: source.updatedAt,
    });
}
/** A saved item (Phase 2.4 feeds this) is always an item: SAVED, stamped with its creation time. */
export function projectSavedItem(source) {
    return Object.freeze({
        id: `saved_item:${source.id}`,
        sourceKind: "SAVED_ITEM",
        sourceId: source.id,
        bucket: "SAVED",
        statusCode: "SAVED",
        title: source.title,
        updatedAt: source.createdAt,
    });
}
const BUCKET_ORDER = {
    NEEDS_YOU: 0,
    WITH_CONCIERGE: 1,
    CONFIRMED: 2,
    SAVED: 3,
};
/**
 * Every source projects independently (no suppression, no merging), then the items are sorted:
 * bucket order, newest first, then id ascending, so the order is total and stable.
 */
export function projectActionCenter(input) {
    const items = [];
    for (const request of input.requests) {
        const item = projectConciergeRequest(request);
        if (item !== null)
            items.push(item);
    }
    if (input.handoff !== null) {
        const item = projectHandoff(input.handoff);
        if (item !== null)
            items.push(item);
    }
    for (const saved of input.savedItems)
        items.push(projectSavedItem(saved));
    items.sort((a, b) => BUCKET_ORDER[a.bucket] - BUCKET_ORDER[b.bucket] ||
        b.updatedAt.getTime() - a.updatedAt.getTime() ||
        (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
    return Object.freeze(items);
}
// --- Action Center v2 (CHT-738, contract 4.7, ADR 0013) ---------------------------------------------------------------
//
// Same rule as v1: derived, never stored. v2 adds payments, a next step, links and a history view. Every table below is
// written out per state, and the SQL read of `@lafayette/db` builds its bucket and state filters from these tables, so the
// database and the projection cannot drift. Text is never taken from staff or a model: the customer sees a closed code and
// the client owns the words.
/** What the customer can do next. The control calls the owning domain route, never the projection. */
export const actionNextSteps = Object.freeze([
    "COMPLETE_DRAFT",
    "ANSWER_QUESTION",
    "OPEN_PAYMENT",
    "OPEN_CONVERSATION",
    "VIEW_CONFIRMATION",
    "VIEW_SAVED",
    "NONE",
]);
/** The history view lists ended requests and payments of this many days, newest first. */
export const ACTION_HISTORY_DAYS = 180;
export const ACTION_TITLE_MAX = 200;
/** The request states in each bucket, written from the table above. Closed states are in no bucket. */
export const requestActionBuckets = REQUEST_BUCKETS;
/** Contract 4.7. `bucket: null` with `listed: true` is HISTORY only. */
export const paymentActionRules = Object.freeze({
    DRAFT: { listed: false, bucket: null, code: null, nextStep: "NONE" },
    READY: { listed: true, bucket: "NEEDS_YOU", code: "PAYMENT_DUE", nextStep: "OPEN_PAYMENT" },
    PENDING_PAYMENT: {
        listed: true,
        bucket: "NEEDS_YOU",
        code: "PAYMENT_IN_PROGRESS",
        nextStep: "OPEN_PAYMENT",
    },
    PAID: { listed: true, bucket: null, code: "PAYMENT_PAID", nextStep: "NONE" },
    PARTIALLY_REFUNDED: {
        listed: true,
        bucket: null,
        code: "PAYMENT_PARTLY_REFUNDED",
        nextStep: "NONE",
    },
    REFUNDED: { listed: true, bucket: null, code: "PAYMENT_REFUNDED", nextStep: "NONE" },
    FAILED: { listed: true, bucket: null, code: "PAYMENT_FAILED", nextStep: "NONE" },
    EXPIRED: { listed: true, bucket: null, code: "PAYMENT_EXPIRED", nextStep: "NONE" },
    CANCELLED: { listed: true, bucket: null, code: "PAYMENT_CANCELLED", nextStep: "NONE" },
});
/** The handoff statuses that show to the customer (a waiting or accepted handoff). */
export const activeHandoffActionStatuses = Object.freeze([
    "REQUESTED",
    "WAITING",
    "ACCEPTED",
]);
export function bucketOrderOf(bucket) {
    return BUCKET_ORDER[bucket];
}
/** A title the schema accepts: trimmed, at most 200 characters, `null` when empty. */
export function actionTitleOf(text) {
    if (text === null)
        return null;
    const trimmed = text.trim();
    if (trimmed === "")
        return null;
    return trimmed.length <= ACTION_TITLE_MAX
        ? trimmed
        : `${trimmed.slice(0, ACTION_TITLE_MAX - 1)}…`;
}
function requestNextStep(source) {
    switch (source.state) {
        case "DRAFT":
            return "COMPLETE_DRAFT";
        case "AWAITING_CUSTOMER":
            return "ANSWER_QUESTION";
        case "PAYMENT_REQUIRED":
            // Always its payment (contract 4.7). The database pins the link (LF022), so a payment row exists; if a read ever lacked it,
            // the screen still shows the status and the way back to the conversation.
            return "OPEN_PAYMENT";
        case "CONFIRMED":
            return "VIEW_CONFIRMATION";
        case "SUBMITTED":
        case "ASSIGNED":
        case "IN_PROGRESS":
            return source.hasConversation ? "OPEN_CONVERSATION" : "NONE";
        default:
            return "NONE";
    }
}
/** Every request state projects, closed ones included (`bucket: null`): the HISTORY view uses them. */
export function projectRequestV2(source) {
    const id = `concierge_request:${source.id}`;
    return Object.freeze({
        id,
        sourceKind: "CONCIERGE_REQUEST",
        sourceId: source.id,
        bucket: requestActionBuckets[source.state],
        statusCode: requestStatusCodes[source.state],
        title: actionTitleOf(source.title),
        nextStep: requestNextStep(source),
        links: Object.freeze({
            target: Object.freeze({ kind: "ACTION", actionId: id }),
            tripId: source.tripId,
            conversation: source.hasConversation,
        }),
        // The payment shows only while the request waits for it.
        payment: source.state === "PAYMENT_REQUIRED" ? source.payment : null,
        updatedAt: source.updatedAt,
    });
}
/** A DRAFT payment is never listed: `null`. Every other status projects (`bucket: null` is history only). */
export function projectPaymentV2(source) {
    const rule = paymentActionRules[source.payment.status];
    if (!rule.listed || rule.code === null)
        return null;
    const id = `payment_request:${source.id}`;
    return Object.freeze({
        id,
        sourceKind: "PAYMENT_REQUEST",
        sourceId: source.id,
        bucket: rule.bucket,
        statusCode: rule.code,
        title: actionTitleOf(source.description),
        nextStep: rule.nextStep,
        links: Object.freeze({
            target: Object.freeze({ kind: "ACTION", actionId: id }),
            tripId: source.tripId,
            conversation: false,
        }),
        payment: source.payment,
        updatedAt: source.updatedAt,
    });
}
/** A released or cancelled handoff is no item: `null`. Same as v1, plus the next step and the links. */
export function projectHandoffV2(source) {
    const statusCode = handoffStatusCodes[source.status];
    if (statusCode === null)
        return null;
    return Object.freeze({
        id: `handoff:${source.id}`,
        sourceKind: "HANDOFF",
        sourceId: source.id,
        bucket: "WITH_CONCIERGE",
        statusCode,
        title: null,
        nextStep: "OPEN_CONVERSATION",
        links: Object.freeze({
            target: Object.freeze({ kind: "CONVERSATION" }),
            tripId: null,
            conversation: true,
        }),
        payment: null,
        updatedAt: source.updatedAt,
    });
}
export function projectSavedItemV2(source) {
    return Object.freeze({
        id: `saved_item:${source.id}`,
        sourceKind: "SAVED_ITEM",
        sourceId: source.id,
        bucket: "SAVED",
        statusCode: "SAVED",
        title: actionTitleOf(source.title),
        nextStep: "VIEW_SAVED",
        links: Object.freeze({
            target: Object.freeze({ kind: "SAVED_ITEM", savedItemId: source.id }),
            tripId: source.tripId,
            conversation: false,
        }),
        payment: null,
        updatedAt: source.createdAt,
    });
}
/** The total order of the ACTIVE view: bucket order, newest first, then id ascending. */
export function compareActionItemsV2(a, b) {
    const bucketA = a.bucket === null ? Number.MAX_SAFE_INTEGER : BUCKET_ORDER[a.bucket];
    const bucketB = b.bucket === null ? Number.MAX_SAFE_INTEGER : BUCKET_ORDER[b.bucket];
    return (bucketA - bucketB ||
        b.updatedAt.getTime() - a.updatedAt.getTime() ||
        (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}
